import moment from 'moment-timezone'

import {
  CATEGORY,
  calendarDate,
  categoryOfEvent,
  isPaidEvent,
  isRegistrationOpen,
  matchesFilter,
  primaryDate,
  programRank
} from 'event-taxonomy'

// Minimum months the calendar can page forward, per product.
const MIN_MONTHS_AHEAD = 3
// Guards against one stray far-future event turning the calendar into a
// hundred empty months.
const MAX_MONTHS_AHEAD = 12

const timeOf = date => (date ? moment(date).valueOf() : Number.MAX_SAFE_INTEGER)

// `events` comes from denormalize() over seamless-immutable-backed query/entity
// state, so it arrives seamless-immutable-wrapped rather than a plain array.
// That library bans mutating methods (.sort, .push, ...) outright, and — more
// subtly — re-wraps the *return value* of non-mutating ones (.filter, .map,
// .reduce), so the immutability follows every derived array too. Spreading
// here produces a genuine plain array via the iterator protocol (bypassing
// those wrapped methods entirely), so everything derived from it downstream —
// in this module and in callers — is safe to .sort()/.push()/etc. normally.
const toPlainArray = events => [...(events || [])]

export const filterByType = (events, type) =>
  toPlainArray(events).filter(event => matchesFilter(event, type))

const byCalendarDateAsc = (a, b) => timeOf(calendarDate(a)) - timeOf(calendarDate(b))

// Live events are the unpaid webinars. Session sales never appear here —
// they get their own Programs & Courses section below, so listing them here
// too would just duplicate it.
export const upcomingLiveEvents = (events, type) =>
  filterByType(events, type)
    .filter(event => !isPaidEvent(event))
    .sort(byCalendarDateAsc)

// The calendar is the one place registration deadlines still show alongside
// live events — on a single timeline, rather than as a second copy of the
// list. They're pinned in regardless of `type`, since that filter only means
// something for webinar subtypes and deadlines aren't one.
export const calendarEvents = (events, type) => {
  const deadlines = filterByType(events, 'deadlines')
  return [...upcomingLiveEvents(events, type), ...deadlines].sort(byCalendarDateAsc)
}

const sortDateOf = event =>
  event.type === 'session_sale'
    ? event.target.open_registration_end_at
    : event.target.start_at

const byDateAsc = (a, b) => timeOf(sortDateOf(a)) - timeOf(sortDateOf(b))

const isProgram = event => categoryOfEvent(event) === CATEGORY.PROGRAM
const isCourse = event => categoryOfEvent(event) === CATEGORY.COURSE
const isChallenge = event => categoryOfEvent(event) === CATEGORY.CHALLENGE

/**
 * Programs first in the priority order product asked for, then courses by start
 * date, then the 14-Day Challenge slotted chronologically into that list —
 * before the first entry dated later than it, otherwise appended.
 */
export const orderProgramsAndCourses = (events, now = moment()) => {
  const eligible = toPlainArray(events)
    .filter(event => isProgram(event) || isCourse(event) || isChallenge(event))
    .filter(event => isRegistrationOpen(event, now))

  const programs = eligible.filter(isProgram).sort((a, b) =>
    (programRank(a.target) - programRank(b.target)) || byDateAsc(a, b)
  )
  const courses = eligible.filter(isCourse).sort(byDateAsc)

  return eligible
    .filter(isChallenge)
    .sort(byDateAsc)
    .reduce((list, challenge) => {
      const challengeTime = timeOf(sortDateOf(challenge))
      const index = list.findIndex(item => timeOf(sortDateOf(item)) > challengeTime)

      return index === -1
        ? [...list, challenge]
        : [...list.slice(0, index), challenge, ...list.slice(index)]
    }, [...programs, ...courses])
}

/**
 * How far the calendar can page. Always at least three months out, extended to
 * cover the furthest event we actually hold, capped so a stray date can't open
 * an unbounded run of empty months.
 */
export const calendarBounds = (events, now = moment()) => {
  const min = now.clone().startOf('month')
  const floor = min.clone().add(MIN_MONTHS_AHEAD, 'months')
  const ceiling = min.clone().add(MAX_MONTHS_AHEAD, 'months')

  const latest = toPlainArray(events).reduce((max, event) => {
    const date = calendarDate(event)
    if (!date) return max
    const parsed = moment(date)
    if (!parsed.isValid()) return max
    return !max || parsed.isAfter(max) ? parsed : max
  }, null)

  // Re-wrap rather than `latest.clone()` — `latest` is built up across a
  // reduce and, unlike `min`/`floor`/`ceiling`, was seen to occasionally reach
  // here as something that duck-typed as a valid moment (passed .isValid())
  // but didn't carry the full prototype in production. moment() on a moment
  // is a normal, well-supported clone, so this is a no-op change in the
  // common case and a safe one in the rare case.
  const dataMax = latest ? moment(latest).startOf('month') : min.clone()

  return { min, max: moment.min(moment.max(dataMax, floor), ceiling) }
}

// Reads ?month=YYYY-MM, clamped into range. Anything missing or unparseable
// falls back to the current month.
export const activeMonth = (value, bounds) => {
  const parsed = value ? moment(value, 'YYYY-MM', true) : null

  if (!parsed || !parsed.isValid()) return bounds.min.clone()
  if (parsed.isBefore(bounds.min, 'month')) return bounds.min.clone()
  if (parsed.isAfter(bounds.max, 'month')) return bounds.max.clone()

  return parsed
}

// Blocks say where they belong; the banner is the only type with a default
// other than the sidebar.
const DEFAULT_PLACEMENT = { EventsPromoBanner: 'main' }

export const placementOf = block =>
  (block.block_content && block.block_content.placement) ||
  DEFAULT_PLACEMENT[block.block_type] ||
  'sidebar'

export const partitionBlocks = (blocks = []) => ({
  main: blocks.filter(block => placementOf(block) === 'main'),
  sidebar: blocks.filter(block => placementOf(block) === 'sidebar')
})

export { primaryDate }
